/* eslint-disable id-length, max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { IntlProvider } from '@godaddy/react-mintl';
import ConditionalGrowl from './lib/ConditionalGrowl';
import Sidebar from './components/Sidebar';
import { UserDataProvider, useRootProps, useUserData } from './components/UserDataProvider';
import { partnerEid, investorEid, internalEid, independentEid } from './lib/requestedHeaderEids';
import { TYPES } from './lib/types';
import HubBanner from './components/HubBanner';

import './header.css';
import intents from './intents.config';

// Intents work
function Style() {
  const css = `
      :root {
        --ux-headerNavigatePrimary-fontWeight: ${intents.headerNavigatePrimary.fontWeight};
        --ux-headerNavigatePrimary-logoColor: ${intents.headerNavigatePrimary.logoColor};
        --ux-headerNavigatePrimary-navButtonHoverColor: ${intents.headerNavigatePrimary.navButtonHoverColor};
        --ux-headerNavigatePrimary-dividerColor: ${intents.headerNavigatePrimary.dividerColor};


        --ux-headerGlobalUtilityButton-foregroundColor: ${intents.headerGlobalUtilityButton.foregroundColor};
        --ux-headerGlobalUtilityButton-backgroundColor: ${intents.headerGlobalUtilityButton.backgroundColor};

        --ux-headerGlobalUtilityButtonHovered-foregroundColor: ${intents.headerGlobalUtilityButtonHovered.foregroundColor};
        --ux-headerGlobalUtilityButtonHovered-backgroundColor: ${intents.headerGlobalUtilityButtonHovered.backgroundColor};
        --ux-headerGlobalUtilityButtonHovered-borderColor: ${intents.headerGlobalUtilityButtonHovered.borderColor};

        --ux-headerGlobalUtilityButtonChosen-foregroundColor: ${intents.headerGlobalUtilityButtonChosen.foregroundColor};
        --ux-headerGlobalUtilityButtonChosen-backgroundColor: ${intents.headerGlobalUtilityButtonChosen.backgroundColor};
        --ux-headerGlobalUtilityButtonChosen-borderColor: ${intents.headerGlobalUtilityButtonChosen.borderColor};


        --ux-headerGlobalUtilityAvatarButton-foregroundColor: ${intents.headerGlobalUtilityAvatarButton.foregroundColor};
        --ux-headerGlobalUtilityAvatarButton-backgroundColor: ${intents.headerGlobalUtilityAvatarButton.backgroundColor};
        --ux-headerGlobalUtilityAvatarButton-borderColor: ${intents.headerGlobalUtilityAvatarButton.borderColor};

        --ux-headerGlobalUtilityAvatarButtonHovered-foregroundColor: ${intents.headerGlobalUtilityAvatarButtonHovered.foregroundColor};
        --ux-headerGlobalUtilityAvatarButtonHovered-backgroundColor: ${intents.headerGlobalUtilityAvatarButtonHovered.backgroundColor};
        --ux-headerGlobalUtilityAvatarButtonHovered-borderColor: ${intents.headerGlobalUtilityAvatarButtonHovered.borderColor};

        --ux-headerGlobalUtilityAvatarButtonChosen-foregroundColor: ${intents.headerGlobalUtilityAvatarButtonChosen.foregroundColor};
        --ux-headerGlobalUtilityAvatarButtonChosen-backgroundColor: ${intents.headerGlobalUtilityAvatarButtonChosen.backgroundColor};
        --ux-headerGlobalUtilityAvatarButtonChosen-borderColor: ${intents.headerGlobalUtilityAvatarButtonChosen.borderColor};

        --ux-headerMenuItem-foregroundColor: ${intents.headerMenuItem.foregroundColor};
        --ux-headerMenuItem-backgroundColor: ${intents.headerMenuItem.backgroundColor};

        --ux-headerMenuItemHovered-foregroundColor: ${intents.headerMenuItemHovered.foregroundColor};
        --ux-headerMenuItemHovered-backgroundColor: ${intents.headerMenuItemHovered.backgroundColor};

        --ux-headerMenuItemChosen-foregroundColor: ${intents.headerMenuItemChosen.foregroundColor};
        --ux-headerMenuItemChosen-backgroundColor: ${intents.headerMenuItemChosen.backgroundColor};

        --ux-headerMenuItemFocused-foregroundColor: ${intents.headerMenuItemFocused.foregroundColor};
        --ux-headerMenuItemFocused-backgroundColor: ${intents.headerMenuItemFocused.backgroundColor};

        --ux-headerInPage-fontWeight: ${intents.headerInPageNav.fontWeight};

        --ux-sidebarResellerShadow-backgroundColor: ${intents.headerNavigatePrimary.shadowColor};

      }
    `;
  return <style type='text/css'>{ css }</style>;
}

// eslint-disable-next-line complexity
const Root = ({
  navigation = [],
  sidebarNav,
  accountTrayNav,
  sidebarFooterContent,
  navigationTop = [],
  navHeading,
  navSubHeadline,
  navBreadcrumb,
  afterNavHeadingLeft,
  beforeNavHeading,
  navHeadingRight,
  sidebarNavLinkHandler,
  primaryActiveNavLink,
  customNotifications,
  accountTrayBottomNav,
  cartItems,
  CartComponent,
  cartLink,
  helpUrl,
  showGlobalUtilityNavHelpLink,
  showGlobalUtilityNavWaffle,
  showGlobalUtilityNavCart,
  showGlobalUtilityNavNotifications,
  sidebarNavTopComponent,
  inPageNavContainerClass
}) => {
  const props = useRootProps();
  const { requestedHeader, privateLabelId, app } = props;

  const isReseller = privateLabelId !== 1;
  const isAfternic = [497036, 1001836].includes(privateLabelId);
  const isMCC = app === 'mcc' && privateLabelId === 1 && requestedHeader === TYPES.investors;

  let appEid = partnerEid;
  if (requestedHeader === TYPES.investors) {
    appEid = investorEid;
  }

  if (requestedHeader === TYPES.independents) {
    appEid = independentEid;
  }

  if (requestedHeader === TYPES.internal) {
    appEid = internalEid;
  }

  const { userData, userDataLoaded } = useUserData();
  const pcx = userDataLoaded && userData && userData.pcx ? userData.pcx : false;
  const showHubBanner = pcx && requestedHeader !== TYPES.partners && !isReseller && !userData.shopperData?.customer?.cookies?.idp?.del;

  return (
    <>
      <Style />
      {showHubBanner && <HubBanner appEid={ appEid }/>}
      <div className='sidebar-micro-ui'>
        <ConditionalGrowl />
        <Sidebar { ...props }
          sidebarNav={ sidebarNav }
          accountTrayNav={ accountTrayNav }
          accountTrayBottomNav={ accountTrayBottomNav }
          footerContent={ sidebarFooterContent }
          appEid={ appEid }
          customNotifications={ customNotifications }
          navigation={ navigation }
          requestedHeader={ requestedHeader }
          navHeading={ navHeading }
          navSubHeadline={ navSubHeadline }
          navBreadcrumb={ navBreadcrumb }
          afterNavHeadingLeft={ afterNavHeadingLeft }
          beforeNavHeading={ beforeNavHeading }
          navHeadingRight={ navHeadingRight }
          isMCC={ isMCC }
          isAfternic={ isAfternic }
          isReseller={ isReseller }
          sidebarNavLinkHandler={ sidebarNavLinkHandler }
          primaryActiveNavLink={ primaryActiveNavLink }
          navigationTop={ navigationTop }
          cartItems={ cartItems }
          CartComponent={ CartComponent }
          cartLink={ cartLink }
          helpUrl={ helpUrl }
          showGlobalUtilityNavHelpLink={ showGlobalUtilityNavHelpLink }
          showGlobalUtilityNavWaffle={ showGlobalUtilityNavWaffle }
          showGlobalUtilityNavCart={ showGlobalUtilityNavCart }
          showGlobalUtilityNavNotifications={ showGlobalUtilityNavNotifications }
          sidebarNavTopComponent={ sidebarNavTopComponent }
          showHubBanner={ showHubBanner }
          inPageNavContainerClass={ inPageNavContainerClass }
        />
      </div>
    </>
  );
};

Root.propTypes = {
  accountTrayNav: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  accountTrayBottomNav: PropTypes.node,
  app: PropTypes.string,
  customer: PropTypes.shape({}),
  customNotifications: PropTypes.arrayOf(PropTypes.object),
  env: PropTypes.string,
  helpUrl: PropTypes.shape({
    href: PropTypes.string,
    target: PropTypes.string
  }),
  cartItems: PropTypes.number,
  CartComponent: PropTypes.node,
  cartLink: PropTypes.string,
  navigation: PropTypes.arrayOf(PropTypes.object),
  navHeading: PropTypes.shape({
    text: PropTypes.string
  }),
  navSubHeadline: PropTypes.oneOfType([
    PropTypes.shape({
      text: PropTypes.string
    }),
    PropTypes.node
  ]),
  navBreadcrumb: PropTypes.shape({
    text: PropTypes.string,
    href: PropTypes.string,
    eid: PropTypes.string,
    onClick: PropTypes.func
  }),
  afterNavHeadingLeft: PropTypes.node,
  beforeNavHeading: PropTypes.node,
  navHeadingRight: PropTypes.node,
  navigationTop: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.node
    ])),
    PropTypes.node
  ]),
  privateLabelId: PropTypes.number,
  requestedHeader: PropTypes.string,
  showGlobalUtilityNavHelpLink: PropTypes.bool,
  showGlobalUtilityNavWaffle: PropTypes.bool,
  showGlobalUtilityNavCart: PropTypes.bool,
  showGlobalUtilityNavNotifications: PropTypes.bool,
  sidebarFooterContent: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  sidebarNav: PropTypes.arrayOf(PropTypes.object),
  urls: PropTypes.shape({
    home: PropTypes.objectOf(PropTypes.string)
  }),
  pollNotificationsTimeout: PropTypes.number,
  reloadNotificationsCounter: PropTypes.number,
  sidebarNavLinkHandler: PropTypes.func,
  primaryActiveNavLink: PropTypes.string,
  customWaffleLinks: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.objectOf(PropTypes.string)
    )
  ),
  sidebarNavTopComponent: PropTypes.node,
  inPageNavContainerClass: PropTypes.string
};

const RootWrapper = (props) => {
  const locale = props.market || 'en';
  const messages = props.messages; // ensure this is a map of IDs→strings for locale
  return (
    <UserDataProvider { ...props }>
      <IntlProvider locale={ locale } messages={ messages }>
        <Root { ...props } />
      </IntlProvider>
    </UserDataProvider>
  );
};

RootWrapper.propTypes = {
  headerMethods: PropTypes.shape({
    updateCartItems: PropTypes.func,
    updateCartComponent: PropTypes.func,
    updateCartLink: PropTypes.func,
    updateHelpUrl: PropTypes.func,
    updateNavigation: PropTypes.func,
    updateAccountTrayNav: PropTypes.func,
    updateAccountTrayBottomNav: PropTypes.func,
    updateSidebarNav: PropTypes.func,
    updateTopNavigation: PropTypes.func,
    updateNavHeading: PropTypes.func,
    updateNavSubHeadline: PropTypes.func,
    updateNavBreadcrumb: PropTypes.func,
    updateAfterNavHeadingLeft: PropTypes.func,
    updateBeforeNavHeading: PropTypes.func,
    updateAfterNavHeadingRight: PropTypes.func,
    updatePollNotificationsTimeout: PropTypes.func,
    reloadNotifications: PropTypes.func,
    onNavLinkClick: PropTypes.func,
    addCustomNotifications: PropTypes.func,
    updateWaffleLinks: PropTypes.func,
    updateSidebarNavTopComponent: PropTypes.func,
    updateInPageNavContainerClass: PropTypes.func
  }),
  accountTrayNav: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  accountTrayBottomNav: PropTypes.node,
  sidebarNav: PropTypes.arrayOf(PropTypes.object),
  market: PropTypes.string,
  messages: PropTypes.objectOf(PropTypes.string)
};

export default RootWrapper;
